"use client";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";

interface InlineEditorProps {
  field: "copy" | "caption" | "hashtags";
  value: string | string[];
  onSave: (value: string | string[]) => void;
  onCancel: () => void;
  isSaving: boolean;
}

export function InlineEditor({ field, value, onSave, onCancel, isSaving }: InlineEditorProps) {
  if (field === "hashtags") {
    return <HashtagEditor value={value as string[]} onSave={onSave} onCancel={onCancel} isSaving={isSaving} />;
  }
  return <TextEditor value={value as string} onSave={(v) => onSave(v)} onCancel={onCancel} isSaving={isSaving} />;
}

function TextEditor({ value: initialValue, onSave, onCancel, isSaving }: {
  value: string; onSave: (value: string) => void; onCancel: () => void; isSaving: boolean;
}) {
  const { t } = useTranslation("pieces");
  const [text, setText] = useState(initialValue);
  return (
    <div className="space-y-2">
      <Textarea value={text} onChange={(e) => setText(e.target.value)} className="min-h-[80px] resize-none" autoFocus />
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{t("pieces:editor.chars", { count: text.length })}</span>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={isSaving}>{t("common:actions.cancel")}</Button>
          <Button size="sm" onClick={() => onSave(text)} disabled={isSaving}>{isSaving ? t("pieces:editor.saving") : t("common:actions.save")}</Button>
        </div>
      </div>
    </div>
  );
}

function HashtagEditor({ value: initialTags, onSave, onCancel, isSaving }: {
  value: string[]; onSave: (value: string | string[]) => void; onCancel: () => void; isSaving: boolean;
}) {
  const { t } = useTranslation("pieces");
  const [tags, setTags] = useState<string[]>(initialTags);
  const [input, setInput] = useState("");

  const addTag = () => {
    const clean = input.trim().replace(/^#/, "");
    if (clean && !tags.includes(clean)) {
      setTags([...tags, clean]);
    }
    setInput("");
  };

  const removeTag = (tag: string) => {
    setTags(tags.filter((t) => t !== tag));
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1">
        {tags.map((tag) => (
          <Badge key={tag} variant="secondary" className="gap-1 text-xs">
            #{tag}
            <button onClick={() => removeTag(tag)} className="ml-1"><X size={10} /></button>
          </Badge>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
          placeholder={t("pieces:editor.hashtagPlaceholder")}
          className="flex-1"
          autoFocus
        />
        <Button variant="outline" size="sm" onClick={addTag}>{t("pieces:editor.add")}</Button>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={isSaving}>{t("common:actions.cancel")}</Button>
        <Button size="sm" onClick={() => onSave(tags)} disabled={isSaving}>{isSaving ? t("pieces:editor.saving") : t("common:actions.save")}</Button>
      </div>
    </div>
  );
}
